import { useState } from 'react';
import { Checkbox } from '@carbon/react';
import { useAtom } from 'jotai';
import { MapUserConfigAtom } from 'components/map/types';
import { useFocusAreas } from './use-focus-areas';
import { CollapsibleSection } from '../collapsible-section';
import style from '../map.module.scss';
import focusAreaStyle from './focus-area.module.scss';
import classNames from 'classnames';
import { focusAreaCategoryColors, defaultFocusAreaColor } from '../map-colors';

export const FocusAreaSelector = ({
  mapConfigAtom,
}: {
  mapConfigAtom: MapUserConfigAtom;
}) => {
  const [mapConfig, setMapConfig] = useAtom(mapConfigAtom);
  const [expandedCategories] = useState<{
    [key: string]: boolean;
  }>({});

  const { isLoading, sharedCategories } = useFocusAreas();

  const isCategoryExpanded = (categoryName: string) =>
    expandedCategories[categoryName] ?? false;

  const getCategoryCheckState = (categoryName: string) => {
    const categorySelections =
      mapConfig.focusAreas?.selections?.[categoryName] || [];
    const categoryAreas =
      sharedCategories.find((cat) => cat.name === categoryName)?.areas || [];

    const allChecked =
      categoryAreas.length > 0 &&
      categoryAreas.every((area) => categorySelections.includes(area.id));
    const someChecked = categoryAreas.some((area) =>
      categorySelections.includes(area.id)
    );

    return {
      checked: allChecked,
      indeterminate: !allChecked && someChecked,
    };
  };

  const getMainCheckState = () => {
    const allCategories = sharedCategories.map((category) => {
      const categoryAreas = category.areas || [];
      const categorySelections =
        mapConfig.focusAreas?.selections?.[category.name] || [];

      const allChecked =
        categoryAreas.length > 0 &&
        categoryAreas.every((area) => categorySelections.includes(area.id));
      const someChecked = categoryAreas.some((area) =>
        categorySelections.includes(area.id)
      );

      return { allChecked, someChecked };
    });

    const allChecked = allCategories.every((cat) => cat.allChecked);
    const someChecked = allCategories.some((cat) => cat.someChecked);

    return {
      checked: allChecked,
      indeterminate: !allChecked && someChecked,
    };
  };

  const toggleCategorySelection = (categoryName: string, checked: boolean) => {
    setMapConfig((current) => {
      const categoryAreas =
        sharedCategories.find((cat) => cat.name === categoryName)?.areas || [];
      const newSelections = {
        ...(current.focusAreas?.selections || {}),
        [categoryName]: checked ? categoryAreas.map((area) => area.id) : [],
      };

      // Check if any areas are selected across all categories
      const hasAnySelections = Object.values(newSelections).some(
        (selections) => selections.length > 0
      );

      return {
        ...current,
        focusAreas: {
          checked: hasAnySelections,
          selections: newSelections,
        },
      };
    });
  };

  const toggleAreaSelection = (categoryName: string, areaId: string) => {
    setMapConfig((current) => {
      const currentSelections = current.focusAreas?.selections || {};
      const categorySelections = currentSelections[categoryName] || [];

      const newCategorySelections = categorySelections.includes(areaId)
        ? categorySelections.filter((id) => id !== areaId)
        : [...categorySelections, areaId];

      const newSelections = {
        ...currentSelections,
        [categoryName]: newCategorySelections,
      };

      // Check if any areas are selected across all categories
      const hasAnySelections = Object.values(newSelections).some(
        (selections) => selections.length > 0
      );

      return {
        ...current,
        focusAreas: {
          checked: hasAnySelections,
          selections: newSelections,
        },
      };
    });
  };

  const isAreaSelected = (categoryName: string, areaId: string) => {
    const categorySelections =
      mapConfig.focusAreas?.selections?.[categoryName] || [];
    return categorySelections.includes(areaId);
  };

  if (isLoading) {
    return <div className={style.loadingIndicator}>Loading focus areas...</div>;
  }

  const mainCheckState = getMainCheckState();

  return (
    <CollapsibleSection
      id="focus-areas"
      title="Focus Areas"
      checked={mainCheckState.checked}
      indeterminate={mainCheckState.indeterminate}
      onChange={(_evt, { checked }) => {
        setMapConfig((current) => {
          // If checked, select all areas in all categories
          const newSelections = checked
            ? sharedCategories.reduce(
                (acc, category) => ({
                  ...acc,
                  [category.name]: (category.areas ?? []).map(
                    (area) => area.id
                  ),
                }),
                {}
              )
            : {};

          return {
            ...current,
            focusAreas: {
              checked,
              selections: newSelections,
            },
          };
        });
      }}
      defaultOpen={false}
    >
      {sharedCategories.map((category) => {
        const categoryState = getCategoryCheckState(category.name);
        const categoryColor =
          focusAreaCategoryColors[category.name] ?? defaultFocusAreaColor;
        return (
          <div
            key={category.name}
            data-category={category.name}
            className={focusAreaStyle.categoryColorWrapper}
            style={
              { '--focus-category-color': categoryColor } as React.CSSProperties
            }
          >
            <CollapsibleSection
              id={`focus-area-category-${category.name}`}
              title={category.name === 'Fault' ? 'Faults' : category.name}
              checked={categoryState.checked}
              indeterminate={categoryState.indeterminate}
              defaultOpen={isCategoryExpanded(category.name)}
              onChange={(_evt, { checked }) =>
                toggleCategorySelection(category.name, checked)
              }
              className={focusAreaStyle.categoryHeader}
            >
              <div
                className={classNames(
                  style.groupChecks,
                  focusAreaStyle.groupChecks
                )}
              >
                {(category.areas ?? []).map((area) => (
                  <div
                    key={area.id}
                    className={focusAreaStyle.areaCheckbox}
                    // An area with its own colour shows it in the legend, the
                    // same rule the map layer applies — the var cascades, so
                    // areas without one keep the category colour.
                    style={
                      area.displayColor
                        ? ({
                            '--focus-category-color': area.displayColor,
                          } as React.CSSProperties)
                        : undefined
                    }
                  >
                    <Checkbox
                      id={`area-${area.id}`}
                      labelText={area.name}
                      checked={isAreaSelected(category.name, area.id)}
                      onChange={() =>
                        toggleAreaSelection(category.name, area.id)
                      }
                    />
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          </div>
        );
      })}
    </CollapsibleSection>
  );
};
